import { useMemo, useRef, useEffect } from '@max/max';
import { navigateTo } from '@max/meituan-uni-navigate';
const isMRN = false;
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
import { TaskStatus, TaskType } from '../types/domain';
import { API_PATH, CacheKey, CompleteResultKind, TaskButtonState } from '../types/shared';
import { BizLine } from '../types/rewardRecords';
import { CLIENT_CONTAINER } from '@lowcode/mcp-protocol/src/base/common';
import { useRequest } from './useRequest';
import { useActivityCache } from './useActivityCache';
import { getRequestBaseParams, getRecExtraParams } from '../utils/request';
import { hasRewardPopup, parseCompleteResult, pickPoiIdAt, pickRecPoiIds, resolveTaskListVo } from '../utils/taskResolve';
import { buildCompleteParams, buildRecParams } from '../utils/taskParams';
import { getTravelUrl, TRAVEL_PAGE_BY_BIZ, TravelPageType } from '../utils/getTravelUrl';
/** R007：浏览停留时长缺省兜底（秒）。未下发 browseDurationTime 时用此默认值 */
const DEFAULT_BROWSE_STAY_SEC = 5;
const DEFAULT_FAIL_TOAST = '完成失败，请稍后重试';
/** R007：浏览任务完成成功 Toast 兜底（运营未配 browseToast 时用） */
const DEFAULT_SUCCESS_TOAST = '任务完成';
/** R007：浏览任务缓存过期（秒）= 半年，与其它活动缓存一致 */
const BROWSE_TASK_CACHE_TTL = 180 * 24 * 60 * 60;
/**
 * R007：任务半页弹窗编排 hook（实例化于 useCityExploreData 内部，先例 useAssistAction）。
 * 职责：弹窗开关、任务 VO 派生、浏览/下单流程（酒店/民宿个性化 poiId）、浏览停留三重保障计时、完成后 Toast 仲裁。
 * 不自己拉 3.5、不自己管大弹窗（复用父级 refresh / popupMap）。
 */
export function useTaskCenter(options) {
    const { feConfig, rawQueryInfoResponse, refresh, getPopupMap, setTaskModalVisible, rewardPopupKeys, selectedCityId } = options;
    const ctx = useMonitorEngineContext();
    const commonParams = ctx._runtimeInfo?.commonParams ?? {};
    const { leaderId } = ctx._runtimeInfo?.queryParams ?? {};
    const { request: taskComplete } = useRequest(API_PATH.TASK_COMPLETE, {
        method: 'POST',
        raw: true
    });
    // R007：浏览任务完成状态持久化到 activity-cache（按 activityId 隔离），完成判定全部以缓存为准
    const { readCache, writeCache } = useActivityCache({
        activityId: feConfig.activityId
    });
    // R007-fix(问题1)：打开弹窗时静默预取 poiId（不弹 loading），点击时只用预取缓存，
    // 未就绪不实时阻塞请求（直接兜底列表页，见 resolveBrowseUrl），避免点开弹窗就打主/奖励/助力接口
    // raw:false —— 让 useRequest 解包到内层 data（推荐接口只需 data 负载，无需按 status 分流）。
    // 若用 raw:true 拿到整层信封 {status,message,data}，pickRecPoiIds 读顶层 remainRecPoiInfo 会恒为空。
    const { request: recPrefetch } = useRequest(API_PATH.PERSONALIZATION_REC, {
        method: 'POST',
        raw: false,
        showLoading: false
    });
    // 任务 VO：派生不进 state，刷新只更新 rawQueryInfoResponse，VO 自动重算
    const taskListVo = useMemo(() => resolveTaskListVo(rawQueryInfoResponse, feConfig.taskCenterConfig), [rawQueryInfoResponse, feConfig.taskCenterConfig]);
    // 内部状态全部用 ref（不渲染）：完成接口防重入 + poiId 预取列表/游标（按 taskId）+ 浏览状态内存镜像
    const completingRef = useRef(false);
    // 每个 taskId 的完整推荐 poiId 列表（按页面生命周期预取一次）+ 下一次取用游标（点击轮取）
    const poiIdListCacheRef = useRef({});
    const poiCursorRef = useRef({});
    // 浏览状态内存镜像：每次读缓存后更新，点击时基于它合并写回（避免点击时再多读一次）
    const browseStateRef = useRef({});
    function toast(content) {
        ctx._uiInteractionAPI.toast.show({
            content
        });
    }
    function buildRequestContext() {
        return {
            activityId: feConfig.activityId,
            isGuest: !!leaderId,
            leaderId,
            recSize: undefined,
            // 推荐接口 cityId 取用户活动参与城市（参与中态选中城市），非端注入预设城市
            participateCityId: selectedCityId
        };
    }
    // ── 浏览任务完成状态缓存（持久化到 activity-cache，跨杀 app / 冷启动）──────────────
    // 读缓存 map，并同步内存镜像（点击时基于镜像合并写回）
    async function readBrowseState() {
        try {
            const raw = await readCache(CacheKey.BrowseTask);
            const map = raw ? JSON.parse(raw) : {};
            browseStateRef.current = map;
            return map;
        }
        catch {
            return browseStateRef.current;
        }
    }
    // 整体覆盖写缓存 map（同步内存镜像）
    async function writeBrowseState(map) {
        browseStateRef.current = map;
        try {
            await writeCache(CacheKey.BrowseTask, JSON.stringify(map), BROWSE_TASK_CACHE_TTL);
        }
        catch { }
    }
    // R007：页面拿到最新任务数据（init/refresh）时对账（info 状态驱动 + clickTime 一次性消费）：
    // 对缓存里有 clickTime 的浏览任务 —— ① info 状态非「未完成」（已完成/不可完成）→ 仅清理残留 clickTime；
    // ② 状态「未完成」→ 先清空该 clickTime 并持久化（一次性消费），再判停留时长，达标才调完成接口。
    // 「先清空再调接口」保证：本函数被重复触发（refresh→effect 重跑 / 多次前台）不会重复完成，也杜绝
    // 「stale clickTime 在无关前台时被误判达标而误完成」。网络异常不重试（clickTime 已清空，用户需重新点）。
    async function checkAndCompleteDueBrowseTasks() {
        if (completingRef.current)
            return;
        // 当前列表里的浏览任务：taskId → TaskInfoVO（含 info 状态）
        const browseTasks = new Map();
        taskListVo.forEach(vo => {
            if (vo.taskType === TaskType.BROWSE_TASK && vo.raw)
                browseTasks.set(vo.taskId, vo.raw);
        });
        if (browseTasks.size === 0)
            return; // 列表未就绪，避免误清
        completingRef.current = true;
        try {
            const map = await readBrowseState();
            const now = Date.now();
            let changed = false;
            // 时长达标、需调完成接口的任务（clickTime 此前已从 map 清空）
            const dueTasks = [];
            for (const taskId of Object.keys(map)) {
                const st = map[taskId];
                if (!st)
                    continue;
                const task = browseTasks.get(taskId);
                if (!task)
                    continue; // 不在当前列表（已移除/非本期）→ 不动，靠 TTL 兜底过期
                if (task.status !== TaskStatus.UNFINISHED) {
                    // 已完成 / 不可完成 → 清理残留 clickTime，不调完成
                    delete map[taskId];
                    changed = true;
                    continue;
                }
                // 状态「未完成」且有 clickTime → 一次性消费：无论时长是否达标都先清空
                const extra = (task.extra ?? {});
                const threshold = (extra.browseTaskExtra?.browseDurationTime ?? DEFAULT_BROWSE_STAY_SEC) * 1000;
                const due = now - st.clickTime >= threshold;
                delete map[taskId];
                changed = true;
                if (due)
                    dueTasks.push(task); // 仅时长达标才进入完成队列
            }
            // 关键：先把「已清空」的 map 持久化，再调完成接口 → 真正一次性消费（杀 app 也不会重复触发）
            if (changed)
                await writeBrowseState(map);
            // 逐个调完成接口（网络异常不重试：clickTime 已清空）
            let completedAny = false;
            for (const task of dueTasks) {
                try {
                    const base = await getRequestBaseParams(commonParams);
                    const res = await taskComplete(buildCompleteParams(base, task, buildRequestContext(), base.userId));
                    const result = parseCompleteResult(res);
                    if (result.ok) {
                        completedAny = true;
                        if (!hasRewardPopup(getPopupMap(), rewardPopupKeys)) {
                            toast(feConfig.taskCenterConfig?.browseToast || DEFAULT_SUCCESS_TOAST);
                        }
                    }
                    else {
                        toast(result.kind === CompleteResultKind.RISK ? feConfig.riskToast || DEFAULT_FAIL_TOAST : feConfig.defaultToast || DEFAULT_FAIL_TOAST);
                    }
                }
                catch (error) {
                    // 网络异常 → 不重试（clickTime 已清空），仅埋点
                    ctx._raptorReport?.monitorReportLog?.('taskCenterCompleteBrowse', error, {
                        level: 'error',
                        category: 'ajaxError'
                    });
                }
            }
            // 完成成功才刷新（刷新会再次触发本 effect，但此时 map 已无对应 clickTime → 不再完成 → 收敛）
            if (completedAny)
                await refresh();
        }
        finally {
            completingRef.current = false;
        }
    }
    // R007-fix：跳转用端容器 —— 宿主未注入 clientContainer 时按运行时环境兜底：
    // 美团 app(MRN) → MEITUAN_APP（直跳 imeituan:// scheme）；其余（H5/小程序/外投）→ OTHER，
    // 由 getTravelUrl 经中间页（convertLinkToAppMiddleOpenLink）唤端，避免在非美团 app 吐出打不开的裸 scheme。
    function resolveContainer() {
        return commonParams?.clientContainer ?? (isMRN ? CLIENT_CONTAINER.MEITUAN_APP : CLIENT_CONTAINER.OTHER);
    }
    // R007：浏览/下单跳转目标页（pages.order）入参 —— 按目标页给各自的城市 key（列表页都需带城市）。
    // cityId 用「用户选择探索的城市」（selectedCityId，参与中态选中的 card group），非设备定位城市；
    // cityName 暂不下发；机票/火车票前置页不带城市（需出发/到达交通参数，待定）。
    function buildOrderPageParams(pageType, cityId) {
        switch (pageType) {
            case TravelPageType.HotelList:
                return {
                    city_id: cityId
                };
            // 酒店列表美团/点评均识别 city_id（collabpage/1984193567）
            case TravelPageType.HomeStayList:
                return {
                    frontCityId: cityId
                };
            // 民宿（zhenguo）列表识别 frontCityId
            case TravelPageType.TicketList:
                return {
                    selectedCityId: cityId
                };
            // 门票列表识别 selectedCityId
            case TravelPageType.VacationList:
                return {
                    cityId
                };
            // 度假列表识别 cityId（优先级最高，collabpage/2696174882）
            case TravelPageType.FlightPrepose:
            case TravelPageType.TrainPrepose:
            default:
                return {};
            // 机票/火车票前置页：需出发/到达 code+名+id，城市单参不足，暂不带（TODO）
        }
    }
    // R007-fix(问题1)：是否需要个性化 poiId —— 浏览任务 + 酒店/民宿 + 后端下发了 供给id(酒店)/标签id(民宿)
    function needsPersonalizedPoiId(task) {
        if (task.taskType !== TaskType.BROWSE_TASK)
            return false;
        const extra = (task.extra ?? {});
        const biz = extra.commonExtra?.biz;
        if (biz !== BizLine.Hotel && biz !== BizLine.HomeStay)
            return false;
        const b = extra.browseTaskExtra ?? {};
        return !!(b.supplyIds?.length || b.dynamicTagIds?.length);
    }
    // R007-fix(问题1)：打开弹窗时静默预取整份 poiId 列表写入缓存（失败静默，点击时兜底实时取）
    async function prefetchPoiId(task) {
        if (!needsPersonalizedPoiId(task) || poiIdListCacheRef.current[task.taskId]?.length)
            return;
        try {
            const base = await getRequestBaseParams(commonParams);
            const recExtra = getRecExtraParams(base, commonParams);
            const poiIds = pickRecPoiIds(await recPrefetch(buildRecParams(base, task, buildRequestContext(), recExtra)));
            if (poiIds.length) {
                poiIdListCacheRef.current[task.taskId] = poiIds;
                if (poiCursorRef.current[task.taskId] == null)
                    poiCursorRef.current[task.taskId] = 0;
            }
        }
        catch (error) {
            // 预取失败静默，点击时 resolveBrowseUrl 会兜底实时取
        }
    }
    // R007：从预取列表里按游标顺序取下一个 poiId（每次点击前进一格，取完一轮回到第一个）；列表为空返回 undefined。
    function takeNextPoiId(taskId) {
        const list = poiIdListCacheRef.current[taskId] ?? [];
        if (!list.length)
            return undefined;
        const cursor = poiCursorRef.current[taskId] ?? 0;
        poiCursorRef.current[taskId] = cursor + 1;
        return pickPoiIdAt(list, cursor);
    }
    // R007：浏览跳转 url —— 优先级：browseTaskExtra.browseUrl（后端为浏览任务指定的落地页，最高）
    // > task.jumpUrl（任务级下发）> 前端拼接。
    // 前端拼接：酒店/民宿用打开弹窗时预取的 poiId（3.18）拼详情页秒跳，poiId 未就绪（预取未完成/失败/接口无返回）
    // 不阻塞等待、直接兜底对应业务列表/前置页；其余业务直接拼列表/前置页。
    // 兜底（pages.order）始终是「引导用户打开美团 app 对应落地页」，已配 scheme 的业务线不会拼出空串。
    async function resolveBrowseUrl(task) {
        const extra = (task.extra ?? {});
        // 0) browseTaskExtra.browseUrl 下发 → 优先级最高
        const browseUrl = extra.browseTaskExtra?.browseUrl;
        if (browseUrl)
            return browseUrl;
        // 1) jumpUrl 下发 → 次优先
        if (task.jumpUrl)
            return task.jumpUrl;
        // 2) 未下发 → 前端拼接
        const container = resolveContainer();
        const bizLine = extra.commonExtra?.biz;
        const pages = bizLine ? TRAVEL_PAGE_BY_BIZ[bizLine] : undefined;
        if (!pages)
            return '';
        // 酒店/民宿浏览：仅用预取缓存里的 poiId 拼详情页；poiId 未就绪不实时阻塞请求，落到下方列表页兜底。
        // 详情页 query key 因业务而异：酒店用 id（collabpage/316184155），民宿（zhenguo-product）用 productId。
        if (bizLine === BizLine.Hotel || bizLine === BizLine.HomeStay) {
            const poiId = takeNextPoiId(task.taskId);
            if (poiId != null) {
                // 酒店详情 query key=id；民宿（zhenguo-product）详情 query key=productId，值均为后端下发 poiId
                const detailParams = bizLine === BizLine.Hotel ? {
                    id: poiId
                } : {
                    productId: poiId
                };
                const detailUrl = getTravelUrl(pages.browse, detailParams, container);
                if (detailUrl)
                    return detailUrl;
            }
        }
        // 兜底列表/前置页（= pages.order，引导用户打开美团 app 对应落地页；
        // 酒店民宿的 pages.browse 是详情页需 poiId，故这里不用 browse）
        return getTravelUrl(pages.order, buildOrderPageParams(pages.order, selectedCityId), container);
    }
    // R007：浏览任务 —— 记录点击到服务端缓存（持久化）→ 拼链跳转。
    // 完成判定不再依赖内存/定时器：回端/冷启动后 init 时由 checkAndCompleteDueBrowseTasks 据缓存对账完成。
    async function onBrowseClick(task) {
        const url = await resolveBrowseUrl(task);
        if (!url) {
            // 设计上 jumpUrl 与前端兜底落地页二者必有其一；走到这里=该业务线 scheme 未配（TODO），上报观测而非误弹 toast
            ctx._raptorReport?.monitorReportLog?.('taskCenterBrowseNoUrl', new Error('no browse url'), {
                level: 'warn',
                category: 'logicError'
            });
            return;
        }
        // 记录本次点击（重复任务再次点击=覆盖该 taskId 条目，刷新 clickTime → 回端可再次对账完成）。
        // 基于内存镜像合并写回，不阻塞跳转（写入窗口极短，杀 app 概率远低于「下游页停留中被杀」）。
        const next = {
            ...browseStateRef.current,
            [task.taskId]: {
                clickTime: Date.now()
            }
        };
        writeBrowseState(next).catch(() => { });
        // 从任务弹窗点击跳出去不关闭半页弹窗（MRN 压栈不卸载），返回页面时弹窗仍保持打开态
        navigateTo({
            url
        });
    }
    // R007：下单任务 —— 跳转；完成判定交后端订单回流，回端刷新一次任务态
    async function onOrderClick(task) {
        // jumpUrl 下发优先；未下发才前端拼对应业务列表/前置页（引导打开美团 app 落地页）
        let url = task.jumpUrl || '';
        if (!url) {
            const extra = (task.extra ?? {});
            const container = resolveContainer();
            const bizLine = extra.commonExtra?.biz;
            const pages = bizLine ? TRAVEL_PAGE_BY_BIZ[bizLine] : undefined;
            url = pages ? getTravelUrl(pages.order, buildOrderPageParams(pages.order, selectedCityId), container) : '';
        }
        if (!url) {
            ctx._raptorReport?.monitorReportLog?.('taskCenterOrderNoUrl', new Error('no order url'), {
                level: 'warn',
                category: 'logicError'
            });
            return;
        }
        // 下单完成交后端订单回流；回端刷新由父级 usePageVisibility → init(true) 覆盖，
        // 不用 navigateTo().then()（压栈即 resolve，刷新会发生在跳转前而非返回后）。
        // 从任务弹窗点击跳出去不关闭半页弹窗（返回页面时弹窗仍保持打开态）。
        navigateTo({
            url
        });
    }
    // R007-fix(问题1)：打开弹窗只展示现有任务数据，不再 refresh()（不打 主接口3.5/奖励3.11/助力 三个接口）；
    // 仅对「酒店/民宿浏览任务且下发了供给/标签 id」的任务静默预取 poiId（3.18），点击「去浏览」时直接用缓存秒跳。
    function openTaskModal() {
        setTaskModalVisible(true);
        taskListVo.forEach(vo => {
            prefetchPoiId(vo.raw).catch(() => { });
        });
    }
    function closeTaskModal() {
        setTaskModalVisible(false);
    }
    // R007：任务点击分发（按钮非未完成态点击无动作；1002 拉人任务不在弹窗配置中，天然不出现）
    async function onTaskClick(task) {
        if (task.buttonState !== TaskButtonState.UNFINISHED)
            return;
        if (task.taskType === TaskType.BROWSE_TASK)
            return onBrowseClick(task.raw);
        if (task.taskType === TaskType.ORDER_TASK)
            return onOrderClick(task.raw);
    }
    // R007：页面拿到最新任务数据（首屏 init / 回端 refresh 后 rawQueryInfoResponse 变化）时，据缓存对账完成浏览任务。
    // 回端完成由父级 usePageVisibility → init(true) → rawQueryInfoResponse 变化驱动本 effect，无需单独的 onPageVisible。
    useEffect(() => {
        if (!rawQueryInfoResponse)
            return;
        checkAndCompleteDueBrowseTasks().catch(() => { });
    }, [rawQueryInfoResponse]);
    return {
        taskListVo,
        openTaskModal,
        closeTaskModal,
        onTaskClick
    };
}
